import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../../context/AuthContext'
import { Eye, EyeOff, Mail, Lock, ArrowRight, Loader } from 'lucide-react'
import toast from 'react-hot-toast'
import benchLogo from '../../assets/Bench_logo.png'

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false)
  const [focusedField, setFocusedField] = useState(null)

  const { login, loginWithToken } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/'
  const autoLoginData = location.state?.autoLogin ? location.state : null

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const getCompanyRedirectPath = (status) => {
    const s = String(status)
    // registration flow:
    // 1. Legal Details -> Status: 'company-register'
    // 2. Profile Building -> Status: 'company-profile'
    // 3. Subscription -> Status: 'true' (or custom if needed)
    // 4. Documents -> Finalize

    if (s === 'true' || s === 'TRUE') return '/company/registration/documents'
    if (s === 'company-profile') return '/company/registration/subscription'
    if (s === 'company-register') return '/company/registration/profile'

    // Default to Step 1
    return '/company/registration/legal-details'
  }

  const getRegistrationRoute = (userRole) => {
    const roleRoutes = {
      vendor: '/vendor/registration',
      company: '/company/registration',
      freelancer: '/freelancer/registration'
    }

    return roleRoutes[userRole?.toLowerCase()] || '/vendor/dashboard'
  }

  const getDashboardRoute = (userRole) => {
    const defaultRoutes = {
      vendor: '/vendor/dashboard',
      company: '/dashboard/company',
      freelancer: '/freelancer/dashboard',
      admin: '/admin/dashboard'
    }
    return defaultRoutes[userRole?.toLowerCase()] || '/dashboard'
  }

  // Auto-login after registration
  const autoLoginAttempted = useRef(false)

  useEffect(() => {
    if (autoLoginData?.autoLogin && autoLoginData?.credentials) {
      // Prevent double invocation
      if (autoLoginAttempted.current) return
      autoLoginAttempted.current = true

      const { email, password, message, token } = autoLoginData.credentials

      // Show success message if provided
      if (message) {
        toast.success(message, { duration: 2000 })
      }

      // Set auto-logging in state
      setIsAutoLoggingIn(true)

      // Auto-login after a short delay to show the loader
      const autoLogin = async () => {
        try {
          // Set form data
          setFormData({ email, password })

          let result;

          if (token) {
            // Use the token directly if available (avoids double login API call)
            result = await loginWithToken(token)
          } else {
            // Fallback to standard login
            result = await login({ email, password })
          }

          if (result.success) {
            const user = result.data
            // Check multiple formats for hasCompletedRegistration
            const hasCompletedRegistration =
              user?.hasCompletedRegistration === true ||
              user?.hasCompletedRegistration === "true" ||
              user?.hasCompletedRegistration === 1 ||
              user?.hasCompletedRegistration === '1' ||
              String(user?.hasCompletedRegistration).toLowerCase() === 'true'
            const userRole = user?.role

            // Redirect based on role and registration status
            // Redirect based on role and registration status
            if (userRole?.toLowerCase() === 'admin') {
              navigate('/admin/dashboard', { replace: true })
            } else if (userRole?.toLowerCase() === 'company') {
              navigate(getCompanyRedirectPath(user?.hasCompletedRegistration), { replace: true })
            } else if (hasCompletedRegistration) {
              // Registration completed - go directly to dashboard
              navigate(getDashboardRoute(userRole), { replace: true })
            } else {
              // Registration not completed - go to registration
              navigate(getRegistrationRoute(userRole), { replace: true })
            }
          } else {
            // If token login failed, try standard login as fallback
            if (token) {
              console.warn("Token login failed, falling back to credentials login")
              const fallbackResult = await login({ email, password })
              if (fallbackResult.success) {
                // handle success logic copied from above, or just reload page to retry?
                // Better to recursive call or just copy logic.
                // For simplicity, let's just fail or rely on user to click login.
                setIsAutoLoggingIn(false)
              } else {
                setIsAutoLoggingIn(false)
              }
            } else {
              setIsAutoLoggingIn(false)
            }
          }
        } catch (error) {
          console.error('Auto-login error:', error)
          setIsAutoLoggingIn(false)
        }
      }

      // Start auto-login after showing the message
      setTimeout(() => {
        autoLogin()
      }, 500)
    }
  }, [autoLoginData, login, loginWithToken, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const result = await login(formData)

      if (result.success) {
        toast.success('Welcome back! Redirecting...')

        const user = result.data
        // Check multiple formats for hasCompletedRegistration
        const hasCompletedRegistration =
          user?.hasCompletedRegistration === true ||
          user?.hasCompletedRegistration === "true" ||
          user?.hasCompletedRegistration === 1 ||
          user?.hasCompletedRegistration === '1' ||
          String(user?.hasCompletedRegistration).toLowerCase() === 'true'
        const userRole = user?.role

        // Remove admin restriction: always route admin to /admin/dashboard
        // Remove admin restriction: always route admin to /admin/dashboard
        if (userRole?.toLowerCase() === 'admin') {
          navigate('/admin/dashboard', { replace: true })
        } else if (userRole?.toLowerCase() === 'company') {
          navigate(getCompanyRedirectPath(user?.hasCompletedRegistration), { replace: true })
        } else if (hasCompletedRegistration) {
          // Registration completed - go directly to dashboard
          navigate(getDashboardRoute(userRole), { replace: true })
        } else if (!hasCompletedRegistration) {
          // Registration not completed - go to registration
          navigate(getRegistrationRoute(userRole), { replace: true })
        } else if (from !== '/') {
          navigate(from, { replace: true })
        } else {
          navigate(getDashboardRoute(userRole), { replace: true })
        }
      }
      else {
        toast.error(result.error || 'Invalid credentials. Please try again.')
      }
    } catch (error) {
      toast.error('Connection error. Please check your network.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 p-4">
      {/* Auto-login Loader Overlay */}
      {isAutoLoggingIn && (
        <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center space-y-4"
          >
            <Loader className="w-12 h-12 text-primary-600 animate-spin" />
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Details Verified!
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Logging you in...
              </p>
            </div>
          </motion.div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="card p-8 shadow-xl space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="flex justify-center mb-4"
            >
              <img
                src={benchLogo}
                alt="AJA Bench Logo"
                className="w-12 h-12 object-contain"
              />
            </motion.div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
              Welcome Back
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Please sign in to your account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="label text-xs">
                Email Address
              </label>
              <div className="relative">
                <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors ${focusedField === 'email' ? 'text-primary-600' : 'text-slate-400'
                  }`}>
                  <Mail className="w-4 h-4" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className="input pl-9 py-2 text-sm"
                  placeholder="name@company.com"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="label text-xs">
                  Password
                </label>
                <Link
                  to="/auth/forgot-password"
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors ${focusedField === 'password' ? 'text-primary-600' : 'text-slate-400'
                  }`}>
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className="input pl-9 pr-9 py-2 text-sm"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="w-3.5 h-3.5 text-primary-600 focus:ring-primary-500 border-slate-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-xs text-slate-600 dark:text-slate-400">
                Remember me
              </label>
            </div>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full py-2.5 text-sm shadow-lg shadow-primary-500/20 group"
            >
              {isLoading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </motion.button>
          </form>

          {/* Social Login */}


          {/* Sign Up Link */}
          <div className="text-center pt-2">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Don't have an account?{' '}
              <Link
                to="/signup/company"
                className="text-primary-600 hover:text-primary-700 font-semibold transition-colors inline-flex items-center group"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default LoginPage